import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Container,
	Paper,
	Stack,
	Typography,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	TextField,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft, faSave } from '@fortawesome/free-solid-svg-icons';

import { DataContext } from '../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../helpers/Helpers';
import { getToken } from '../../../api/Account';
import {
	addArqueoUserAsync,
	getDenominationsAsync,
	getDataArqueoUserAsync
} from '../../../api/ArqueoCaja';
import { getSucursalByUserAsync, getusersBySucursalAsync } from '../../../api/Users';
import { getTipoCambioHoyAsync } from '../../../api/TipoCambio';
import moment from 'moment/moment';
import GastosGestorModal from './GastosGestorModal';

/* ================== HELPERS ================== */

const formatter = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

const buildDetalle = (data) =>
	data.map((item) => ({
		id: item.id,
		description: item.description,
		valor: item.valor,
		cantidad: '',
		total: 0
	}));

/* ================== COMPONENT ================== */

const ArqueoUserAdd = () => {
	const navigate = useNavigate();
	const token = getToken();
	const ruta = getRuta();

	const { setIsLoading, setIsUnautorized, selectedSucursal, setSelectedSucursal } =
		useContext(DataContext);

	/* ===== STATES ===== */

	const [sucursalList, setSucursalList] = useState([]);
	const [userList, setUserList] = useState([]);
	const [selectedUser, setSelectedUser] = useState('');
	const [selectedFecha, setSelectedFecha] = useState(new Date());

	const [dataArqueoUser, setDataArqueoUser] = useState(null);

	const [denominationList, setDenominationList] = useState([]);
	const [detalleArqueo, setDetalleArqueo] = useState([]);
	const [totalBilletaje, setTotalBilletaje] = useState(0);

	const [tcCompra, setTcCompra] = useState(0);
	const [saldoInicial, setSaldoInicial] = useState('');
	const [montoDepositos, setMontoDepositos] = useState('');
	const [observaciones, setObservaciones] = useState('');
	const [gastosModalOpen, setGastosModalOpen] = useState(false);

	const n = (v) => Number(v) || 0;

	const saldoInicialN = n(saldoInicial);
	const gastosN = n(dataArqueoUser?.montoGastos);
	const depositosN = n(montoDepositos);

	const recuperacionEfectivoN = n(dataArqueoUser?.montoRecuperacionEfectivo);
	const abonosN = n(dataArqueoUser?.montoRecuperacionTransferencia);
	const desembolsosN = n(dataArqueoUser?.montoDesembolsos);

	// Solo la recuperación en efectivo representa dinero físico en caja;
	// los abonos por transferencia no forman parte de la entrega.
	const entrega = React.useMemo(() => {
		return saldoInicialN + recuperacionEfectivoN - (gastosN + desembolsosN + depositosN);
	}, [saldoInicialN, recuperacionEfectivoN, gastosN, desembolsosN, depositosN]);

	const diferenciaCalculada = React.useMemo(() => {
		return totalBilletaje - entrega;
	}, [totalBilletaje, entrega]);

	/* ===== ERROR HANDLER ===== */
	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') {
			setIsUnautorized(true);
			return true;
		}
		simpleMessage(result.error, 'error');
		return true;
	};

	/* ===== INIT ===== */

	useEffect(() => {
		const init = async () => {
			setIsLoading(true);

			const suc = await getSucursalByUserAsync(token);
			if (!suc.statusResponse && handleError(suc)) return;

			let sucursalId = 0;
			if (selectedSucursal === 0) {
				setSelectedSucursal(suc.data.result[0].id);
				sucursalId = suc.data.result[0].id;
			} else {
				sucursalId = selectedSucursal;
			}
			setSucursalList(suc.data.result);
			setSelectedSucursal(sucursalId);

			await Promise.all([loadDenominaciones(), loadUsuarios(sucursalId), loadTipoCambio()]);

			setIsLoading(false);
		};

		init();
	}, []);

	/* ===== LOADERS ===== */

	const loadDenominaciones = async () => {
		const res = await getDenominationsAsync(token);
		if (!res.statusResponse && handleError(res)) return;

		setDenominationList(res.data.result);
		setDetalleArqueo(buildDetalle(res.data.result));
	};

	const loadUsuarios = async (sucursalId) => {
		const res = await getusersBySucursalAsync(token, sucursalId);
		if (!res.statusResponse && handleError(res)) return;

		setUserList(res.data.result);
	};

	const loadTipoCambio = async () => {
		const res = await getTipoCambioHoyAsync(token);
		if (!res.statusResponse && handleError(res)) return;

		setTcCompra(res.data.compra);
	};

	/* ===== EVENTS ===== */

	const onChangeSucursal = async (value) => {
		setSelectedSucursal(value);
		await Promise.all([loadUsuarios(value), loadDenominaciones()]);
	};

	const onChangeUserDate = async (userId, fecha) => {
		setSelectedUser(userId);
		setSelectedFecha(fecha);
		if (!userId) return;

		setIsLoading(true);
		const res = await getDataArqueoUserAsync(token, { userId, fecha });
		if (!res.statusResponse && handleError(res)) return;

		setDataArqueoUser(res.data.result);
		setIsLoading(false);
	};

	const onEditDetalle = useCallback(
		(id, valor, cantidad) => {
			const qty = Number(cantidad);
			if (!Number.isFinite(qty)) return;

			const result = detalleArqueo.reduce(
				(acc, item) => {
					let subtotal = item.total;

					if (item.id === id) {
						subtotal = valor * qty;
						if (item.id === 2) subtotal *= tcCompra;
						subtotal = Math.round(subtotal * 100) / 100;

						acc.detalle.push({ ...item, cantidad: qty, total: subtotal });
					} else {
						acc.detalle.push(item);
					}

					acc.total += subtotal;
					return acc;
				},
				{ detalle: [], total: 0 }
			);

			result.total = Math.round(result.total * 100) / 100;

			setDetalleArqueo(result.detalle);
			setTotalBilletaje(result.total);
		},
		[detalleArqueo, tcCompra, dataArqueoUser]
	);

	const onChangeMonto = (value, setter) => {
		if (value === '') return setter('');
		if (/^\d*\.?\d*$/.test(value)) setter(value);
	};

	const saveArqueo = async () => {
		if (selectedUser === 0 || selectedUser === '') {
			simpleMessage('Debe seleccionar un usuario', 'warning');
			return;
		}
		const data = {
			aqueoUserDetails: detalleArqueo.map((d) => ({
				demonimacionId: d.id,
				cantidad: Number(d.cantidad) || 0,
				total: d.total
			})),
			fechaArqueo: moment(selectedFecha).format('YYYY-MM-DD'),
			saldoInicial: Number(saldoInicial) || 0,
			recuperacion: dataArqueoUser.montoRecuperacionEfectivo,
			recuperacionTransferencia: dataArqueoUser.montoRecuperacionTransferencia,

			depositos: Number(montoDepositos) || 0,
			montoGastos: dataArqueoUser.montoGastos || 0,
			montoDesembolso: dataArqueoUser.montoDesembolsos,

			diferencia: diferenciaCalculada,
			efectivo: totalBilletaje,
			gestorId: selectedUser,
			sucursalId: selectedSucursal,
			observaciones: observaciones,
			tipoCambio: tcCompra
		};

		setIsLoading(true);
		const res = await addArqueoUserAsync(token, data);
		if (!res.statusResponse && handleError(res)) return;

		setDetalleArqueo(buildDetalle(denominationList));
		setTotalBilletaje(0);

		setIsLoading(false);
	};

	/* ===== UI ===== */

	return (
		<>
		<Container maxWidth="xl">
			<Paper sx={{ p: 2 }}>
				{/* HEADER */}
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Stack direction="row" spacing={2} alignItems="center">
						<Button onClick={() => navigate(`${ruta}/arqueouser`)} variant="outlined">
							<FontAwesomeIcon icon={faChevronCircleLeft} />
							&nbsp;Regresar
						</Button>

						<Typography fontWeight="bold" color="#00a152">
							Arqueo a Usuario
						</Typography>
					</Stack>

					<Stack direction="row" spacing={2} alignItems="center">
						{/* ✅ SELECTOR DE SUCURSAL */}
						<FormControl size="small" sx={{ minWidth: 220 }}>
							<InputLabel>Sucursal</InputLabel>
							<Select
								value={selectedSucursal}
								label="Sucursal"
								onChange={(e) => onChangeSucursal(e.target.value)}
							>
								{sucursalList.map((s) => (
									<MenuItem key={s.id} value={s.id}>
										{s.description}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Stack>

				<hr />

				<Grid container spacing={2}>
					{/* ================= LEFT ================= */}
					<Grid item xs={12} md={6}>
						<Paper sx={{ p: 2 }}>
							<Stack spacing={1}>
								<Typography fontWeight="bold" color="#00a152">
									Usuario
								</Typography>

								<hr />

								<FormControl fullWidth size="small">
									<InputLabel>Usuario</InputLabel>
									<Select
										value={selectedUser}
										label="Usuario"
										onChange={(e) =>
											onChangeUserDate(e.target.value, selectedFecha)
										}
									>
										{userList.map((u) => (
											<MenuItem key={u.id} value={u.id}>
												{u.fullName}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<Stack direction="row" justifyContent="space-between" spacing={2}>
									<DesktopDatePicker
										label="Fecha"
										value={selectedFecha}
										onChange={(v) => onChangeUserDate(selectedUser, v)}
										renderInput={(params) => (
											<TextField {...params} fullWidth size="small" />
										)}
									/>

									<TextField
										fullWidth
										size="small"
										label="Saldo Inicial"
										value={saldoInicial}
										onChange={(e) =>
											onChangeMonto(e.target.value, setSaldoInicial)
										}
									/>
								</Stack>

								<Stack direction="row" justifyContent="space-between" mt={2}>
									<Typography fontWeight="bold">
										Recuperación en Efectivo
									</Typography>
									<Typography fontWeight="bold">
										{formatter.format(recuperacionEfectivoN)}
									</Typography>
								</Stack>

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">
										Recuperación en Depósitos
									</Typography>
									<Typography fontWeight="bold">
										{formatter.format(abonosN)}
									</Typography>
								</Stack>

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">Total Ingresos</Typography>
									<Typography fontWeight="bold" color="#00a152">
										{formatter.format(
											(Number(saldoInicial) || 0) +
												recuperacionEfectivoN +
												abonosN
										)}
									</Typography>
								</Stack>

								<hr />

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">Monto Gastos</Typography>
									<Typography fontWeight="bold">
										{formatter.format(gastosN)}
									</Typography>
								</Stack>

								<Button
									variant="outlined"
									size="small"
									disabled={!selectedUser}
									style={{ color: '#f50057', borderColor: '#f50057' }}
									onClick={() => setGastosModalOpen(true)}
								>
									Ver Gastos
								</Button>

								<Stack direction="row" justifyContent="space-between" mt={1}>
									<Typography fontWeight="bold">Total Desembolsos</Typography>
									<Typography fontWeight="bold">
										{formatter.format(dataArqueoUser?.montoDesembolsos || 0)}
									</Typography>
								</Stack>

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">Total Egresos</Typography>
									<Typography fontWeight="bold" color="#f50057">
										{formatter.format(
											gastosN +
												(dataArqueoUser?.montoDesembolsos || 0) +
												(Number(montoDepositos) || 0)
										)}
									</Typography>
								</Stack>

								<hr />

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">Entrega</Typography>
									<Typography fontWeight="bold" color="#2979ff">
										{formatter.format(entrega)}
									</Typography>
								</Stack>
								<hr />

								<Stack direction="row" justifyContent="space-between">
									<Typography fontWeight="bold">Diferencia</Typography>
									<Typography
										fontWeight="bold"
										color={diferenciaCalculada === 0 ? '#00a152' : '#ffc400'}
									>
										{formatter.format(diferenciaCalculada)}
									</Typography>
								</Stack>

								<TextField
									fullWidth
									size="small"
									label="Observaciones"
									value={observaciones}
									onChange={(e) =>
										setObservaciones(e.target.value.toLocaleUpperCase())
									}
									multiline
								/>
							</Stack>
						</Paper>
					</Grid>

					{/* ================= RIGHT ================= */}
					<Grid item xs={12} md={6}>
						<Paper sx={{ p: 2 }}>
							<Stack spacing={1}>
								<Stack direction="row" justifyContent="space-between" mb={0}>
									<Typography fontWeight="bold">Billetaje</Typography>
									<Typography variant="body2" color="#ffc400">
										Compra: {formatter.format(tcCompra)}
									</Typography>
								</Stack>

								<hr />

								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ py: 0.25 }}>
													Denominación
												</TableCell>
												<TableCell align="center" sx={{ py: 0.25 }}>
													Cantidad
												</TableCell>
												<TableCell align="center" sx={{ py: 0.25 }}>
													Total
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{detalleArqueo.map((row) => (
												<TableRow
													key={row.id}
													sx={{
														'& td': {
															py: 0.25,
															px: 1
														}
													}}
												>
													<TableCell>{row.description}</TableCell>
													<TableCell align="center">
														<TextField
															size="small"
															value={row.cantidad}
															onChange={(e) =>
																onEditDetalle(
																	row.id,
																	row.valor,
																	e.target.value
																)
															}
															inputProps={{
																style: {
																	padding: '2px 6px',
																	height: 26,
																	textAlign: 'center'
																}
															}}
														/>
													</TableCell>
													<TableCell align="center">
														{formatter.format(row.total)}
													</TableCell>
												</TableRow>
											))}

											<TableRow
												sx={{
													'& td': {
														py: 0.25,
														px: 1,
														fontWeight: 'bold',
														color: '#00a152'
													}
												}}
											>
												<TableCell colSpan={2}>TOTAL BILLETAJE</TableCell>
												<TableCell align="center">
													{formatter.format(totalBilletaje)}
												</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</TableContainer>
							</Stack>
						</Paper>
					</Grid>

					<Grid item xs={12}>
						<Button
							fullWidth
							variant="outlined"
							onClick={saveArqueo}
							style={{
								color: '#2979ff',
								borderColor: '#2979ff',

								borderRadius: 30
							}}
						>
							<FontAwesomeIcon
								icon={faSave}
								style={{ marginRight: 5, fontSize: 15 }}
							/>
							Guardar Arqueo
						</Button>
					</Grid>
				</Grid>
			</Paper>
		</Container>

			<GastosGestorModal
				open={gastosModalOpen}
				onClose={setGastosModalOpen}
				gestorId={selectedUser}
				sucursalId={selectedSucursal}
				fecha={selectedFecha}
				readOnly={false}
				onChange={() => onChangeUserDate(selectedUser, selectedFecha)}
			/>
		</>
	);
};

export default ArqueoUserAdd;
